import { type PipelineCardDetail, PipelineRouteKey } from '@/pages/pipelines/common/types';

import {
  isDirectInvestmentCard,
  isPipelineNewInvestorParentCard,
  NEW_INVESTOR_PARENT_STAGE,
  normalizeInvestmentCardPresentation,
  normalizeNewInvestorParentStage,
} from './normalize-card';
import { getInvestmentStageLabel } from './stage-presentation';

const NEW_INVESTOR_PARENT_INCLUDED_DETAIL_ROW_KEYS = new Set(['stage', 'assignee']);
const DIRECT_INVESTMENT_OMITTED_DETAIL_ROW_KEYS = new Set(['fee', 'feeEligibility', 'window']);
const DIRECT_INVESTMENT_OMITTED_TIMELINE_STAGES = new Set(['LinkClicked', 'DueDiligenceViewed']);
const NEW_INVESTOR_PARENT_DETAIL_INDICATOR_KEYS = new Set([
  'following',
  'followingFund',
  'communication',
  'inCommunication',
]);

export const normalizeInvestmentPipelineDetail = (
  detail: PipelineCardDetail,
): PipelineCardDetail => {
  if (detail.pipeline !== PipelineRouteKey.Investment) return detail;

  const renamedDetail = normalizeInvestmentDetailRows(detail);
  if (isPipelineNewInvestorParentCard(renamedDetail)) {
    return normalizeInvestmentCardPresentation(normalizeNewInvestorParentDetail(renamedDetail));
  }
  if (isDirectInvestmentCard(renamedDetail)) {
    return normalizeInvestmentCardPresentation(normalizeDirectInvestmentDetail(renamedDetail));
  }

  return normalizeInvestmentCardPresentation(renamedDetail);
};

const normalizeNewInvestorParentDetail = (detail: PipelineCardDetail): PipelineCardDetail => {
  const indicators = detail.indicators ?? [];
  const sections = detail.sections ?? [];
  const timeline = detail.timeline ?? [];

  return {
    ...normalizeNewInvestorParentStage(detail),
    indicators: indicators.filter(
      (indicator) => !NEW_INVESTOR_PARENT_DETAIL_INDICATOR_KEYS.has(indicator.key),
    ),
    sections: sections
      .map((section) => ({
        ...section,
        rows: section.rows
          .filter((row) => NEW_INVESTOR_PARENT_INCLUDED_DETAIL_ROW_KEYS.has(row.key))
          .map((row) =>
            row.key === 'stage'
              ? { ...row, value: getInvestmentStageLabel(NEW_INVESTOR_PARENT_STAGE) }
              : row,
          ),
      }))
      .filter((section) => section.rows.length > 0),
    timeline: timeline.filter((item) => item.stage === NEW_INVESTOR_PARENT_STAGE),
  };
};

const normalizeDirectInvestmentDetail = (detail: PipelineCardDetail): PipelineCardDetail => ({
  ...detail,
  sections: (detail.sections ?? [])
    .map((section) => ({
      ...section,
      rows: section.rows.filter((row) => !DIRECT_INVESTMENT_OMITTED_DETAIL_ROW_KEYS.has(row.key)),
    }))
    .filter((section) => section.rows.length > 0),
  timeline: (detail.timeline ?? []).filter(
    (item) => !DIRECT_INVESTMENT_OMITTED_TIMELINE_STAGES.has(item.stage),
  ),
});

const normalizeInvestmentDetailRows = (detail: PipelineCardDetail): PipelineCardDetail => ({
  ...detail,
  sections: (detail.sections ?? []).map((section) => ({
    ...section,
    rows: section.rows.map((row) => {
      if (row.key === 'offer') return { ...row, label: 'Fund' };
      if (row.key === 'stage' && row.value) {
        return { ...row, value: getInvestmentStageLabel(row.value) };
      }

      return row;
    }),
  })),
});
